import { Link } from "react-router-dom";
import { CalendarDays, ChevronRight, Filter, Landmark, Plus, Search, User } from "lucide-react";
import { toast } from "sonner";
import { CASES, STAGES } from "@/data/demo";
import { useAuth } from "@/lib/auth";
import { Btn, Card, PageHeader, StageBadge } from "@/components/common";
import { NextHearingCard } from "@/components/NextHearingCard";

const demo = () => toast("Demo mode — this action is visual only.");

const CaseCard = ({ c, isClient }) => {
  const pct = Math.round(((c.stage + 1) / STAGES.length) * 100);
  return (
    <Link to={`/cases/${c.id}`} data-testid={`case-card-${c.id}`} className="group block">
      <Card className="p-5 transition-[box-shadow,transform,border-color] duration-200 group-hover:-translate-y-0.5 group-hover:border-amber-200 group-hover:shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gold">{c.number}</div>
            <h3 className="mt-1 font-heading text-base font-semibold text-navy sm:text-lg">{c.title}</h3>
          </div>
          <ChevronRight size={20} className="mt-1 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5" />
        </div>
        <div className="mt-3 flex flex-col gap-1.5 text-sm text-slate-500">
          {!isClient && <span className="flex items-center gap-2"><User size={14} />{c.clientName}</span>}
          <span className="flex items-center gap-2"><Landmark size={14} />{c.court}</span>
          <span className="flex items-center gap-2"><CalendarDays size={14} />Next hearing: {c.nextHearing}</span>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2"><StageBadge stage={c.stage} testId={`case-stage-badge-${c.id}`} /></div>
        {isClient && (
          <div className="mt-4">
            <div className="mb-1.5 flex justify-between text-xs font-medium text-slate-500"><span>Case progress</span><span>Step {c.stage + 1} of {STAGES.length}</span></div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div data-testid={`case-progress-${c.id}`} className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </Card>
    </Link>
  );
};

export default function Cases() {
  const { user } = useAuth();
  const isClient = user.user_type === "client";
  const list = isClient ? CASES.filter((c) => c.clientId === "cl1") : CASES;
  return (
    <div>
      <PageHeader title={isClient ? "My Cases" : "Cases"} subtitle={isClient ? "Track where each of your cases stands." : `${list.length} cases in your practice`}>
        {!isClient && (
          <>
            <Btn data-testid="filter-cases-button" variant="outline" onClick={demo}><Filter size={16} />Filter</Btn>
            <Btn data-testid="new-case-button" variant="gold" onClick={demo}><Plus size={16} />New Case</Btn>
          </>
        )}
      </PageHeader>
      {isClient && <NextHearingCard />}
      {!isClient && (
        <div className="relative mb-5">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="case-search-input" placeholder="Search by case title, number or client" className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 text-sm outline-none transition-[border-color,box-shadow] focus:border-amber-400 focus:ring-4 focus:ring-amber-100" />
        </div>
      )}
      <div data-testid="cases-list" className="grid gap-4 md:grid-cols-2">
        {list.map((c) => <CaseCard key={c.id} c={c} isClient={isClient} />)}
      </div>
    </div>
  );
}
